// import { Spinner } from '@pegasus/components';
import classNames from 'classnames';
import IFrameResizer from 'iframe-resizer-react';
import qs from 'qs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router';

import { usePopup } from 'common/hooks';
import { CryptoPayPopup } from 'common/popups';

import { actionLoadingGetBalance } from 'views/wallet-new/redux/actions';

import { routes } from 'routes/routes.constants';

import { composeUrl } from 'services/composeUrl';

import { getCssVariable } from 'utils/getCssVariable';
import { memoizedComponent } from 'utils/memoizeComponent';

import './index.css';
import { FrameEventType, FrameEvents, FrameProps } from './types';

export const Frame: React.FC<React.PropsWithChildren<FrameProps>> = memoizedComponent(
  ({ url, onLoad, className, dataAttribute = '', params = {}, onReceiveMessage: onReceiveMessageFromProps }) => {
    const { culture } = useSelector((state: DefaultRootState) => state.common.resources);

    const DEFAULT_FRAME_PARAMS = useMemo(
      () => ({
        'header-height': getCssVariable('--header-height'),
        lang: culture
      }),
      [culture],
    );
    const urlWithDefaultParams = useMemo(() => composeUrl(url, DEFAULT_FRAME_PARAMS), [DEFAULT_FRAME_PARAMS, url]);

    const [link, setLink] = useState(url);
    const [hiddenFrame, setHiddenFrame] = useState(false);
    const [height, updateHeight] = useState<number | string | undefined>('100%');
    const [loaded, setLoaded] = useState(false);
    const { push, goBack, replace } = useHistory();
    const { show, hide } = usePopup();
    const dispatch = useDispatch();

    useEffect(() => {
      setLink(urlWithDefaultParams);
    }, [urlWithDefaultParams]);

    const onFrameLoaded = useCallback(() => {
      setLoaded(true);
      if (onLoad) onLoad();
    }, [onLoad]);

    const query = qs.stringify(params, { addQueryPrefix: true });

    const onReply = useCallback((_params) => {
      const frame = document.getElementById('frame');

      if (frame instanceof HTMLIFrameElement) {
        frame?.contentWindow?.postMessage(_params, '*');
      }
    }, []);

    const onReceiveMessage = useCallback(
      (e: MessageEvent<FrameEventType>) => {
        const { type, amount = params.amount, currency = params.currency, url: redirectUrl } = e.data;

        onReply(e.data);

        if (type in FrameEvents) console.log('type => ', type, e.data);

        onReceiveMessageFromProps?.(e, onReply);

        switch (type) {
          case FrameEvents.LOGOUT:
            setHiddenFrame(true);
            break;
          case FrameEvents.BACK:
            goBack();
            break;
          case FrameEvents.CLOSE_POPUP:
            hide();
            break;
          case FrameEvents.GET_CRYPTOPAY_LINK:
            show(CryptoPayPopup());
            break;
          case FrameEvents.REDIRECT_CRYPTOPAY:
            push(routes.getCryptoPayPage());
            break;
          case FrameEvents.HOME:
            push(routes.getRoot());
            break;
          case FrameEvents.WALLET:
            push(routes.getTransactionsPage());
            break;
          case FrameEvents.LOYALTY:
            push(routes.getLoyaltyPage());
            break;
          case FrameEvents.EXCHANGE:
            push(
              generatePath(routes.getExchangeFormPage(), {
                symbol: currency,
                ...params,
                amount,
                currency,
              }),
            );
            break;
          case FrameEvents.SPOT:
            push(
              generatePath(routes.getSpotAssetPage(), {
                ...params,
                amount,
                currency,
              }),
            );
            break;
          case FrameEvents.BUY_CRYPTO:
            replace(
              generatePath(routes.getBuyCryptoFormPage(), {
                symbol: currency,
                ...params,
                amount,
                currency,
              }) + query,
            );
            break;
          case FrameEvents.DEPOSIT_CRYPTO:
            push(generatePath(routes.getReceivePage(), { currency, ...params }) + query);
            break;
          case FrameEvents.VERIFICATION:
            push(routes.getVerificationLevelPage());
            break;
          case FrameEvents.VERIFICATION_LEVELS:
            window.location.assign(`${routes.getWebsite()}${redirectUrl ?? ''}`);
            break;
          case FrameEvents.BALANCE_UPDATE:
            dispatch(actionLoadingGetBalance());
            break;
          case FrameEvents.SUCCESS:
            console.log('Success event');
            break;
          case FrameEvents.RESIZE:
            updateHeight(e.data.height);
            break;
          case FrameEvents.REDIRECT:
            window.location.assign(`${routes.getWebsite()}${redirectUrl ?? ''}`);
            break;
          default:
            break;
        }
      },
      [params, onReceiveMessageFromProps, onReply, goBack, hide, show, push, replace, query, dispatch],
    );

    useEffect(
      function subscribeFrameEvents() {
        window.addEventListener('message', onReceiveMessage);
        return () => {
          window.removeEventListener('message', onReceiveMessage);
        };
      },
      [onReceiveMessage],
    );

    if (hiddenFrame) return null;

    const frameClassNames = classNames({
      frame: true,
      'frame--hidden': !loaded,
      [`${className}`]: className,
    });

    return (
      <div style={{ height: '100%' }} id='frame-container'>
        {/* {!loaded && <Spinner />} */}
        <IFrameResizer
          id='frame'
          className={frameClassNames}
          data-test={dataAttribute}
          style={{ width: '1px', minWidth: '100%', minHeight: height }}
          title={link}
          src={link}
          onLoad={onFrameLoaded}
          frameBorder={0}
          scrolling
          allow='payment'
        />
      </div>
    );
  },
);
